/**
 * How a connection's badge on the Settings page is decided, with no network
 * call of its own.
 *
 * Its own module, not a section of platformAccountService: this is a pure
 * function over stored fields, it is the one thing in that file with no
 * Supabase client in it, and the file reached the 800-line ceiling the moment
 * last_verified_at gave it a third kind of evidence to weigh.
 * platformAccountService re-exports it, so every existing caller and both of
 * its test files are unaffected by the move.
 *
 * @module services/platformAccountStatus
 */

import type { PlatformAccount } from "@/types";

/**
 * How long a successful "Test connection" stands as proof.
 *
 * It goes stale at all because of an asymmetry between the two kinds of
 * evidence this function reads. A failed SYNC records itself — sync_error is
 * written, and the branch below flips the account to "error" without being
 * asked. A failed VERIFICATION records nothing: the route deliberately writes
 * last_verified_at only on success, so the column is monotone and no later
 * event can contradict it. "Never stale" would therefore mean one green test,
 * then a revoked token, and a row that reads "active" until a sync happens to
 * fail — and in production no sync has ever completed, so nothing would.
 *
 * Thirty days, not seven: re-testing a healthy connection every week is a nag
 * people learn to click past, and it is inside the 60-day life of a Facebook
 * long-lived token, so a credential heading for death by age gets looked at
 * again before it gets there. The cost of being wrong is one line asking for a
 * check that takes one click, which is the "unverified" state doing its job.
 */
const VERIFICATION_VALID_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Passive connection health, derived WITHOUT a network call. The live
 * "Test connection" action is the source of truth; this is the honest
 * best-guess from stored fields alone:
 *   - no credential of any kind → "disconnected"; nothing can work. A credential
 *     is an access token OR a refresh token (which can mint one) OR a
 *     service-account connection, whose key lives in
 *     media_platform_account_secrets and never in access_token
 *   - hard-expired access token AND no refresh_token → definitely reconnect
 *   - a recorded sync_error → the last sync actually failed
 *   - access token expired but refreshable → "active" ONLY if a sync or a live
 *     verification succeeded after it expired (proof it still works);
 *     otherwise "unverified"
 *   - connected but never synced and never verified → no positive evidence yet
 *     → "unverified"
 *   - otherwise (a valid token that has synced or verified without error)
 *     → "active"
 *
 * Why the "synced after expiry" test rather than just trusting the refresh
 * token: the sync path does NOT auto-refresh (MediaSyncOrchestrator never calls
 * client.refreshToken()), so a stored refresh_token is no guarantee the
 * credential still works. We only claim "active" on evidence — a sync that
 * happened after the token's expiry — never on an unverified assumption. This
 * is exactly what lets the user spot a silently-dropped connection.
 *
 * A VERIFICATION after the expiry earns the same treatment, and for a stronger
 * reason than a sync does: the platform was handed this exact credential and
 * accepted it, after the date we predicted it would die. The expiry is a
 * prediction (for Facebook it is one this app computes itself, in
 * usePlatformAccounts, because the OAuth response does not carry one); the
 * verification is a measurement. A measurement after the predicted death beats
 * the prediction.
 *
 * What a verification does NOT outrank, each because it was a real bug:
 *   - "disconnected": no credential still wins, whatever any timestamp says.
 *   - "error": the last sync genuinely failed and that is the actionable thing.
 *     A credential can pass a health check and still be failing to deliver.
 *   - "expired" with no refresh token: unchanged. Past its date with nothing to
 *     renew it, this connection is on its way out whatever one more call
 *     managed, and "reconnect" is the honest instruction. Note that a sync
 *     after the expiry does not rescue that branch either — it returns before
 *     lastSyncedAt is read — so the two kinds of evidence stay consistent.
 *
 * Exported for direct unit testing — keep this a pure function.
 */
export function derivePassiveStatus(input: {
  hasAccessToken: boolean;
  connectionMethod?: string | null;
  tokenExpiresAt?: string | null;
  hasRefreshToken: boolean;
  syncError?: string | null;
  lastSyncedAt?: string | null;
  /**
   * When the platform last ACCEPTED this credential in a live check. Written
   * server-side only (the browser holds no privilege on the column), and only
   * on success — see VERIFICATION_VALID_MS for what that costs us.
   */
  lastVerifiedAt?: string | null;
  now?: Date;
}): PlatformAccount["status"] {
  // Checked before everything else: an account with no credential cannot sync
  // whatever the other fields say. This mattered — 16 of 17 accounts on one
  // deployment had no access token and every one of them reported "active",
  // because a seeded last_synced_at was the only evidence being consulted and
  // nothing here ever asked whether there was a credential to begin with.
  //
  // "Credential" is deliberately broader than access_token. A refresh token can
  // mint one, and a service-account connection keeps its key in
  // media_platform_account_secrets — both store an empty access_token and both
  // work. Testing access_token alone would have reported the two Google
  // connections here as disconnected while they were syncing fine.
  const hasCredential =
    input.hasAccessToken || input.hasRefreshToken || input.connectionMethod === "service_account";
  if (!hasCredential) return "disconnected";

  const now = input.now ?? new Date();
  const expiresAt = input.tokenExpiresAt ? new Date(input.tokenExpiresAt) : null;
  const tokenExpired = Boolean(expiresAt && expiresAt < now);

  if (tokenExpired && !input.hasRefreshToken) return "expired";
  if (input.syncError) return "error";

  const verifiedAt = input.lastVerifiedAt ? new Date(input.lastVerifiedAt) : null;
  // Inside the window, so it is still worth something. Both branches below ask
  // the same question of the same boolean — a verification that has aged out
  // must not quietly keep one branch green.
  const verificationCounts =
    verifiedAt != null && now.getTime() - verifiedAt.getTime() <= VERIFICATION_VALID_MS;

  if (tokenExpired) {
    // Expired but refreshable: a sync or a verification AFTER the expiry proves
    // it still works.
    const syncedSinceExpiry =
      input.lastSyncedAt != null &&
      expiresAt != null &&
      new Date(input.lastSyncedAt) >= expiresAt;
    const verifiedSinceExpiry =
      verificationCounts && verifiedAt != null && expiresAt != null && verifiedAt >= expiresAt;
    return syncedSinceExpiry || verifiedSinceExpiry ? "active" : "unverified";
  }
  if (!input.lastSyncedAt && !verificationCounts) return "unverified";
  return "active";
}
